import React from 'react';
import { Command, LogOut, Menu, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar, AdminSection } from './AdminSidebar';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface AdminLayoutProps {
  children: React.ReactNode;
  activeSection: AdminSection;
  onSelectSection: (section: AdminSection) => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ 
  children, 
  activeSection, 
  onSelectSection 
}) => {
  const { userEmail, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  const userInitials = userEmail
    ?.split('@')[0]
    ?.split('.')
    ?.map((n: string) => n[0])
    ?.join('')
    ?.toUpperCase() || '??';

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  const handleMobileSelect = (section: AdminSection) => {
    onSelectSection(section);
    setIsMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <AdminSidebar 
          activeSection={activeSection} 
          onSelectSection={onSelectSection} 
        />
      </div>

      {/* Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Header */}
        <header className="bg-card border-b h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-10">
          <div className="flex items-center">
            {/* Mobile Sidebar Trigger */}
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden mr-2">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <AdminSidebar 
                  activeSection={activeSection} 
                  onSelectSection={handleMobileSelect}
                  className="w-full border-none" 
                />
              </SheetContent>
            </Sheet>

            <button 
              onClick={handleDashboardClick}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <Command className="h-6 w-6 text-primary" />
              <h1 className="ml-3 text-xl font-bold tracking-tight">JLS Apps</h1>
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            <span className="text-sm text-muted-foreground hidden sm:inline-block">{userEmail}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </Button>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">{userInitials}</span>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto p-6 md:p-8 bg-muted/10">
          <div className="max-w-6xl mx-auto animate-in fade-in duration-500">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
